import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface UserRecord {
  _id?: string;
  targets?: string[];
  dashboards?: string[];
  [key: string]: unknown;
}

interface UserState {
  uid: number;
  userName: string;
  email: string;
  password: string;
  customerId: number;
  customerName: string;
  user: UserRecord | null;
  isTargetsLoading: boolean;
  isDashboardsLoading: boolean;
  isCustomersLoading: boolean;
  isUsersLoading: boolean;
  isUserLoading: boolean;
  isTreeLoading: boolean;
  error: boolean;
  errorMessage: string;
  selectedTargets: string[];
  selectedDashboards: string[];
  dashboards: unknown[];
  isUserValid?: boolean;
  // Written via editUser (arbitrary form field), read by the editor.
  disabled?: boolean;
}

const initialState: UserState = {
  uid: 0,
  userName: '',
  email: '',
  password: '',
  customerId: 0,
  customerName: '',
  user: null,
  isTargetsLoading: false,
  isDashboardsLoading: false,
  isCustomersLoading: false,
  isUsersLoading: false,
  isUserLoading: false,
  isTreeLoading: false,
  error: false,
  errorMessage: '',
  selectedTargets: [],
  selectedDashboards: [],
  dashboards: [],
  disabled: false,
};

// M6: RTK createSlice. (The old SHOW_DISABLED_USERS case is dropped — that value was
// write-only/dead here; the `users` slice owns showDisabledUsers now.)
const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    requestUser(state) {
      return { ...state, isUserLoading: true, error: false, errorMessage: '' };
    },
    requestUserError(state, action: PayloadAction<{ errorMessage?: string }>) {
      return { ...state, isUserLoading: false, error: true, errorMessage: action.payload.errorMessage || '_empty_users_error_' };
    },
    requestUserSuccess(state, action: PayloadAction<UserRecord>) {
      return {
        ...state,
        isUserLoading: false,
        error: false,
        errorMessage: '',
        user: action.payload,
        selectedTargets: action.payload.targets ?? [],
        selectedDashboards: action.payload.dashboards ?? [],
      };
    },
    // Writes an arbitrary form field by name; the computed key defeats a strict
    // return type, so we assert UserState (all writable fields are declared above).
    editUser(state, action: PayloadAction<{ property: string; value: unknown }>) {
      return { ...state, [action.payload.property]: action.payload.value } as UserState;
    },
    validateUser(state, action: PayloadAction<boolean>) {
      return { ...state, isUserLoading: false, error: false, errorMessage: '', isUserValid: action.payload };
    },
    resetUser() {
      return initialState;
    },
    requestSaveUser(state) {
      return { ...state, isUserLoading: true, error: false, errorMessage: '' };
    },
    requestSaveUserSuccess(state, action: PayloadAction<UserRecord>) {
      return { ...state, isUserLoading: false, error: false, errorMessage: '', user: action.payload };
    },
    requestSaveUserError(state, action: PayloadAction<{ errorMessage?: string }>) {
      return { ...state, isUserLoading: false, error: true, errorMessage: action.payload.errorMessage || '_empty_customer_error_' };
    },
    requestDeleteUser(state) {
      return { ...state, isUserLoading: true, error: false, errorMessage: '' };
    },
    requestDeleteUserSuccess(state, action: PayloadAction<UserRecord>) {
      return { ...state, isUserLoading: false, error: false, errorMessage: '', user: action.payload };
    },
    requestDeleteUserError(state, action: PayloadAction<{ errorMessage?: string }>) {
      return { ...state, isUserLoading: false, error: true, errorMessage: action.payload.errorMessage || '_empty_customer_error_' };
    },
    setSelectedRootTargets(state, action: PayloadAction<string[]>) {
      return { ...state, selectedTargets: action.payload };
    },
    setSelectedDashboards(state, action: PayloadAction<string[]>) {
      return { ...state, selectedDashboards: action.payload };
    },
    requestSaveAuth0User(state) {
      return { ...state, isUserLoading: true, error: false, errorMessage: '' };
    },
    requestSaveAuth0UserSuccess(state, action: PayloadAction<UserRecord>) {
      return { ...state, isUserLoading: false, error: false, errorMessage: '', user: action.payload };
    },
    requestSaveAuth0UserError(state, action: PayloadAction<{ errorMessage?: string }>) {
      return { ...state, isUserLoading: false, error: true, errorMessage: action.payload.errorMessage || '_empty_customer_error_' };
    },
  },
});

export const {
  requestUser, requestUserError, requestUserSuccess,
  editUser, validateUser, resetUser,
  requestSaveUser, requestSaveUserSuccess, requestSaveUserError,
  requestDeleteUser, requestDeleteUserSuccess, requestDeleteUserError,
  setSelectedRootTargets, setSelectedDashboards,
  requestSaveAuth0User, requestSaveAuth0UserSuccess, requestSaveAuth0UserError,
} = userSlice.actions;
export default userSlice.reducer;
